/*
 * Copyright (c) 2023. Bluemate B.V.
 */

import {downloadFile, nullify, ResponseData} from '@dvrd/dvr-controls';
import IDate from '@dvrd/idate';
import {xor} from 'lodash';
import {ColumnFilterApplyClickEvent} from 'primereact/column';
import {DataTablePageEvent, DataTableStateEvent} from 'primereact/datatable';
import {MultiSelectChangeEvent} from 'primereact/multiselect';
import React, {useContext, useEffect, useRef, useState} from 'react';
import {sendGaPageView} from '../../util/analytics';
import {onSetCrumbs} from '../../util/events';
import {
    ActivateHandle,
    IdleState,
    PrimeSorting,
    SalesDashboardOrder,
    SalesDashboardPeriod,
    SalesDashboardProduct
} from '../../util/interfaces';
import {
    exportSalesDashboard,
    getSalesDashboardChartData,
    getSalesDashboardProducts,
    getSalesDashboardStats
} from '../../util/requestSender';
import {
    ColumnConfig,
    defaultSalesFilter,
    getSelectableColumns,
    getSalesDashboardColumns,
    SalesFilter
} from '../../util/salesDashboardUtil';
import {createErrorMessage, orderDirectionFromInt} from '../../util/utils';
import {AppContext} from '../misc/appContext';
import {useIdleObserver} from '../misc/idleObserver';
import ProductCostsController from '../productCosts/productCostsController';
import CustomPeriodController from './customPeriod/customPeriodController';
import ProductCostsUploadController from './productCostsUpload/productCostsUploadController';
import SalesDashboard from './salesDashboard';
import {showDialog} from '@dvrd/dvr-controls';
import {useTranslation} from 'react-i18next';

type LoadConfig = {
    search?: string;
    limit?: number;
    page?: number;
    sortField?: string;
    sortOrder?: number | null;
    filters?: SalesFilter;
    period?: SalesDashboardPeriod;
    loadProducts?: boolean;
    loadStats?: boolean;
    loadChart?: boolean;
    noLoader?: true;
    combineAccounts?: Set<string>;
};

function orderFromSortField(field: string): SalesDashboardOrder {
    if (field === 'revenue') return SalesDashboardOrder.REVENUE;
    else if (field === 'revenue_incl') return SalesDashboardOrder.REVENUE_INCL;
    else if (field === 'quantity') return SalesDashboardOrder.QUANTITY;
    else if (field === 'num_orders') return SalesDashboardOrder.NUM_ORDERS;
    else if (field === 'avg_price') return SalesDashboardOrder.PRICE;
    else if (field === 'avg_price_incl') return SalesDashboardOrder.PRICE_INCL;
    else if (field === 'commission') return SalesDashboardOrder.COMMISSION;
    else if (field === 'cost_price') return SalesDashboardOrder.COST_PRICE;
    else if (field === 'cost') return SalesDashboardOrder.COST;
    else if (field === 'profit') return SalesDashboardOrder.PROFIT;
    else if (field === 'profit_per_product') return SalesDashboardOrder.PROFIT_PER_PRODUCT;
    else if (field === 'margin') return SalesDashboardOrder.MARGIN;
    else if (field === 'commission_percentage') return SalesDashboardOrder.COMMISSION_PERCENTAGE;
    else if (field === 'avg_commission') return SalesDashboardOrder.AVG_COMMISSION;
    else if (field === 'returned_quantity') return SalesDashboardOrder.RETURNED_QUANTITY;
    return SalesDashboardOrder.TITLE;
}

const AUTO_RENEW_TIMEOUT = 60 * 1000; // 1 minutes
const SEARCH_DEBOUNCE = 500;
const DEFAULT_LIMIT = 10;
const PERIOD_FORMAT = 'YYYY-MM-DD';
const defaultPeriod = SalesDashboardPeriod.TODAY;

export default function SalesDashboardController() {
    const context = useContext(AppContext);
    const {account} = context.accountContext;
    const {t, i18n} = useTranslation();
    const [combineAccounts, setCombineAccounts] = useState<Set<string>>(new Set());
    const [salesData, setSalesData] = useState<Array<SalesDashboardProduct>>([]);
    const [loadingStats, setLoadingStats] = useState(true);
    const [loadingProducts, setLoadingProducts] = useState(true);
    const [loadingChart, setLoadingChart] = useState(true);
    const [period, setPeriod] = useState<SalesDashboardPeriod>(defaultPeriod);
    const [search, setSearch] = useState('');
    const [totalRevenue, setTotalRevenue] = useState(0);
    const [totalQuantity, setTotalQuantity] = useState(0);
    const [totalProfit, setTotalProfit] = useState(0);
    const [totalMargin, setTotalMargin] = useState(0);
    const [prevRevenue, setPrevRevenue] = useState(0);
    const [prevQuantity, setPrevQuantity] = useState(0);
    const [prevProfit, setPrevProfit] = useState(0);
    const [prevMargin, setPrevMargin] = useState(0);
    const [totalRows, setTotalRows] = useState(0);
    const [order, setOrder] = useState('quantity');
    const [orderDirection, setOrderDirection] = useState<PrimeSorting>(-1);
    const [firstRowNum, setFirstRowNum] = useState(0);
    const [limit, setLimit] = useState(DEFAULT_LIMIT);
    const [page, setPage] = useState(0);
    const [visibleColumns, setVisibleColumns] = useState<Array<ColumnConfig>>(
        getSelectableColumns(getSalesDashboardColumns()).slice()
    );
    useEffect(() => {
        const refresh = () => {
            const newAll = getSelectableColumns(getSalesDashboardColumns());
            setVisibleColumns(prev => {
                if (!prev.length) return newAll.slice();
                const visibleFields = new Set(prev.map(col => col.field));
                return newAll.filter(col => visibleFields.has(col.field));
            });
        };
        i18n.on('languageChanged', refresh);
        return () => {
            i18n.off('languageChanged', refresh);
        };
    }, []);
    const [filters, setFilters] = useState<SalesFilter>(defaultSalesFilter);
    const [chartData, setChartData] = useState<Record<string, number>>({});
    const searchDebounce = useRef<number | undefined>(undefined);
    const costPriceImportRef = useRef<ActivateHandle>(null);
    const customPeriodRef = useRef<ActivateHandle>(null);
    const costsRef = useRef<ActivateHandle>(null);
    const customPeriodStart = useRef<IDate | null>(null);
    const customPeriodEnd = useRef<IDate | null>(null);
    const autoRenewTimeout = useRef<number | undefined>(undefined);
    useIdleObserver(onIdleStateChange);

    function onCombineAccount(selectedAccountIDs: Array<string>) {
        const selected = new Set(selectedAccountIDs);
        if (!xor(Array.from(selected), Array.from(combineAccounts)).length) return;
        setCombineAccounts(selected);
        loadAll({combineAccounts: selected});
    }

    function onConfigureCosts(product: SalesDashboardProduct) {
        return function () {
            costsRef.current?.activate(product);
        };
    }

    function onSubmitCustomPeriod(periodStart: IDate, periodEnd: IDate) {
        const period = SalesDashboardPeriod.CUSTOM;
        customPeriodStart.current = periodStart;
        customPeriodEnd.current = periodEnd;
        loadAll({period, loadStats: true, loadChart: true, loadProducts: true});
    }

    function onChangePeriod(period: SalesDashboardPeriod) {
        if (period === SalesDashboardPeriod.CUSTOM) return customPeriodRef.current?.activate();
        loadAll({period, loadStats: true, loadChart: true, loadProducts: true});
    }

    function onToggleColumnVisibility(evt: MultiSelectChangeEvent) {
        const allColumns = getSelectableColumns(getSalesDashboardColumns()).slice();
        const selectedColumns = evt.value;
        const orderedSelectedColumns = allColumns.filter(col =>
            selectedColumns.some((sCol: ColumnConfig) => sCol.field === col.field)
        );
        setVisibleColumns(orderedSelectedColumns);
    }

    function onSort(evt: DataTableStateEvent) {
        const sortField = evt.sortField;
        let orderDirection = evt.sortOrder;
        if (sortField === 'title' && sortField !== order) orderDirection = 1;
        setOrder(sortField);
        setOrderDirection(orderDirection);
        loadAll({sortField, sortOrder: orderDirection, loadStats: false, loadChart: false, loadProducts: true});
    }

    function onChangeSearch(evt: React.ChangeEvent<HTMLInputElement>) {
        clearSearchDebounce();
        const value = evt.target.value;
        setSearch(value);
        searchDebounce.current = window.setTimeout(() => {
            loadAll({page: 0, search: value, loadStats: false, loadChart: false, loadProducts: true});
        }, SEARCH_DEBOUNCE);
    }

    function onChangeFilter(evt: ColumnFilterApplyClickEvent) {
        const {field, constraints} = evt;
        const newFilters = {
            ...filters,
            [field]: constraints
        };
        setFilters(newFilters);
        loadAll({filters: newFilters, loadStats: false, loadChart: false, loadProducts: true});
    }

    function onChangePage(evt: DataTablePageEvent) {
        if (evt.page !== undefined) setPage(evt.page);
        setLimit(evt.rows);
        setFirstRowNum(evt.first);
        loadAll({page: evt.page, limit: evt.rows, loadStats: false, loadChart: false, loadProducts: true});
    }

    function onClearFilter(field: keyof Omit<SalesDashboardProduct, 'vat_percentage'>) {
        return function () {
            const newFilters = {...filters, [field]: defaultSalesFilter[field]};
            setFilters(newFilters);
            loadAll({filters: newFilters, loadStats: false, loadChart: false, loadProducts: true});
        };
    }

    function onUploadProductCosts() {
        costPriceImportRef.current?.activate();
    }

    function onReload() {
        loadAll();
    }

    function onExport() {
        if (!account) return;
        setLoadingProducts(true);
        const periodStart = period === SalesDashboardPeriod.CUSTOM ? customPeriodStart.current : undefined;
        const periodEnd = period === SalesDashboardPeriod.CUSTOM ? customPeriodEnd.current : undefined;
        const exportColumns = visibleColumns.map((col: ColumnConfig) => col.field);
        exportSalesDashboard({
            data: {
                period,
                order: orderFromSortField(order),
                filters,
                account_id: account.id,
                order_direction: orderDirectionFromInt(orderDirection),
                search,
                period_start: periodStart?.format(PERIOD_FORMAT),
                period_end: periodEnd?.format(PERIOD_FORMAT),
                export_columns: exportColumns,
                combine_account_ids: nullify(Array.from(combineAccounts))
            },
            callback: (data: ResponseData, response: Response) => {
                if (data.status === 'success') {
                    response.blob().then((blob: Blob) => {
                        const header = response.headers.get('Content-Disposition');
                        let fileName = 'sales-dashboard-export.xlsx';
                        if (header)
                            fileName = header.substring(
                                header.indexOf('filename=') + 'filename='.length,
                                header.length
                            );
                        downloadFile(blob, fileName);
                        setLoadingProducts(false);
                    });
                } else {
                    setLoadingProducts(false);
                    showDialog(
                        createErrorMessage(
                            data.message ??
                                t('salesDashboard.exportFailedMsg', 'Het exporteren van de data is niet gelukt.')
                        ),
                        t('salesDashboard.exportFailedTitle', 'Exporteren mislukt')
                    );
                }
            }
        });
    }

    function loadAll(config?: LoadConfig) {
        clearAutoRenew();
        const loaders: Array<Promise<void>> = [];
        if ([undefined, true].includes(config?.loadProducts)) loaders.push(loadProducts(config));
        if ([undefined, true].includes(config?.loadStats)) loaders.push(loadStats(config));
        if ([undefined, true].includes(config?.loadChart)) loaders.push(loadChartData(config));
        Promise.allSettled(loaders).then(() => {
            scheduleAutoRenew(config);
        });
    }

    function loadProducts(config?: LoadConfig): Promise<void> {
        return new Promise((resolve, reject) => {
            if (!account) return reject();
            const _period = config?.period ?? period;
            const _search = config?.search ?? search;
            const _order = orderFromSortField(config?.sortField ?? order);
            const _orderDirection = orderDirectionFromInt(config?.sortOrder ?? orderDirection);
            const _filters = config?.filters ?? filters;
            const _limit = config?.limit ?? limit;
            const _page = config?.page ?? page;
            const _combineAccounts = config?.combineAccounts ?? combineAccounts;
            const periodStart = _period === SalesDashboardPeriod.CUSTOM ? customPeriodStart.current : undefined;
            const periodEnd = _period === SalesDashboardPeriod.CUSTOM ? customPeriodEnd.current : undefined;
            if (!config?.noLoader) setLoadingProducts(true);
            getSalesDashboardProducts({
                data: {
                    period: _period,
                    order: _order,
                    filters: _filters,
                    account_id: account.id,
                    limit: _limit,
                    page: _page,
                    order_direction: _orderDirection,
                    search: _search,
                    period_start: periodStart?.format(PERIOD_FORMAT),
                    period_end: periodEnd?.format(PERIOD_FORMAT),
                    combine_account_ids: nullify(Array.from(_combineAccounts))
                },
                callback: (data: ResponseData) => {
                    setLoadingProducts(false);
                    if (data.success) {
                        setSalesData(data.sales_data);
                        setTotalRows(data.total);
                    }
                    resolve();
                }
            });
        });
    }

    function loadStats(config?: LoadConfig): Promise<void> {
        return new Promise((resolve, reject) => {
            if (!account) return reject();
            const _period = config?.period ?? period;
            const periodStart = _period === SalesDashboardPeriod.CUSTOM ? customPeriodStart.current : undefined;
            const periodEnd = _period === SalesDashboardPeriod.CUSTOM ? customPeriodEnd.current : undefined;
            const _combineAccounts = config?.combineAccounts ?? combineAccounts;
            if (!config?.noLoader) setLoadingStats(true);
            getSalesDashboardStats({
                data: {
                    account_id: account.id,
                    period: _period,
                    period_start: periodStart?.format(PERIOD_FORMAT),
                    period_end: periodEnd?.format(PERIOD_FORMAT),
                    combine_account_ids: nullify(Array.from(_combineAccounts))
                },
                callback: (data: ResponseData) => {
                    setLoadingStats(false);
                    if (data.success) {
                        const {
                            revenue,
                            quantity,
                            profit,
                            margin,
                            prev_revenue,
                            prev_quantity,
                            prev_profit,
                            prev_margin
                        } = data;
                        setTotalRevenue(revenue);
                        setTotalQuantity(quantity);
                        setTotalProfit(profit);
                        setTotalMargin(margin);
                        setPrevRevenue(prev_revenue);
                        setPrevQuantity(prev_quantity);
                        setPrevProfit(prev_profit);
                        setPrevMargin(prev_margin);
                    }
                    resolve();
                }
            });
        });
    }

    function loadChartData(config?: LoadConfig): Promise<void> {
        return new Promise((resolve, reject) => {
            if (!account) return reject();
            const _period = config?.period ?? period;
            const periodStart = _period === SalesDashboardPeriod.CUSTOM ? customPeriodStart.current : undefined;
            const periodEnd = _period === SalesDashboardPeriod.CUSTOM ? customPeriodEnd.current : undefined;
            const _combineAccounts = config?.combineAccounts ?? combineAccounts;
            if (!config?.noLoader) setLoadingChart(true);
            getSalesDashboardChartData({
                data: {
                    account_id: account.id,
                    period: _period,
                    period_start: periodStart?.format(PERIOD_FORMAT),
                    period_end: periodEnd?.format(PERIOD_FORMAT),
                    combine_account_ids: nullify(Array.from(_combineAccounts))
                },
                callback: (data: ResponseData) => {
                    setLoadingChart(false);
                    setPeriod(_period);
                    if (data.success) setChartData(data.chart_data);
                    resolve();
                }
            });
        });
    }

    function clearSearchDebounce() {
        window.clearTimeout(searchDebounce.current);
        searchDebounce.current = undefined;
    }

    function clearAutoRenew() {
        window.clearTimeout(autoRenewTimeout.current);
        autoRenewTimeout.current = undefined;
    }

    function scheduleAutoRenew(config?: LoadConfig) {
        const _period = config?.period ?? period;
        if (_period === SalesDashboardPeriod.TODAY) {
            config = {...config, noLoader: true};
            autoRenewTimeout.current = window.setTimeout(loadAll, AUTO_RENEW_TIMEOUT, config);
        }
    }

    function onIdleStateChange(currentState: IdleState) {
        if (currentState === IdleState.IDLE) clearAutoRenew();
        else if (period === SalesDashboardPeriod.TODAY) loadAll({noLoader: true});
    }

    useEffect(() => {
        loadAll();
        return function () {
            clearSearchDebounce();
            clearAutoRenew();
            setCombineAccounts(new Set());
        };
    }, [account]);

    useEffect(() => {
        sendGaPageView('/sales-dashboard');
        onSetCrumbs([{label: t('salesDashboard.crumb', 'Sales Dashboard'), route: '/sales-dashboard'}]);
        return function () {
            clearSearchDebounce();
            clearAutoRenew();
        };
    }, []);

    return (
        <>
            <SalesDashboard
                onChangePeriod={onChangePeriod}
                onSort={onSort}
                onChangeSearch={onChangeSearch}
                onChangePage={onChangePage}
                onExport={onExport}
                onToggleColumn={onToggleColumnVisibility}
                onChangeFilter={onChangeFilter}
                onClearFilter={onClearFilter}
                onReload={onReload}
                onClickImportProductCosts={onUploadProductCosts}
                onConfigureCosts={onConfigureCosts}
                onCombineAccount={onCombineAccount}
                combineAccounts={combineAccounts}
                loadingStats={loadingStats}
                period={period}
                totalRevenue={totalRevenue}
                totalQuantity={totalQuantity}
                totalProfit={totalProfit}
                totalMargin={totalMargin}
                data={salesData}
                totalRows={totalRows}
                firstRowNum={firstRowNum}
                sortField={order}
                orderDirection={orderDirection}
                search={search}
                limit={limit}
                visibleColumns={visibleColumns}
                filters={filters}
                loadingProducts={loadingProducts}
                prevQuantity={prevQuantity}
                prevMargin={prevMargin}
                prevProfit={prevProfit}
                prevRevenue={prevRevenue}
                chartData={chartData}
                loadingChart={loadingChart}
            />
            <ProductCostsUploadController ref={costPriceImportRef} onSuccess={onReload} />
            <CustomPeriodController ref={customPeriodRef} onSubmit={onSubmitCustomPeriod} />
            <ProductCostsController ref={costsRef} onReload={onReload} />
        </>
    );
}
